/**
 * The behaviour behind `p.sound` and `p.desktop`.
 *
 * Both settings existed in the schema from the start and drove nothing: the
 * page offered two toggles that were stored and never read. That is worse than
 * omitting them, because it teaches people their settings do not work and then
 * they stop changing the ones that do.
 *
 * SCOPING. `p.desktop`'s hint is "Scoped by role plus ownership", so a person is
 * notified about a thread that is either unowned (nobody has picked it up, so
 * it is everyone's problem) or owned by them. A thread another tech is already
 * handling is deliberately silent — a console that pings for every event in the
 * building gets muted within a day, and then it warns nobody about anything.
 */

export interface NotifyInput {
  /** A short line describing what happened. */
  title: string
  body: string
  /** The signed-in person's staff id. */
  meId: string | null
  /** Who owns the thread, if anyone. */
  ownerId: string | null
  sound: boolean
  desktop: boolean
}

export interface NotifyDeps {
  /** Injected for tests, and because jsdom has neither. */
  AudioContextImpl?: typeof AudioContext
  NotificationImpl?: typeof Notification
  /** Whether the document is currently visible. A desktop notification for a window you are looking at is noise. */
  isHidden?: () => boolean
}

/** Whether this person should be told about this thread at all. */
export function shouldNotify(meId: string | null, ownerId: string | null): boolean {
  if (ownerId === null) return true
  return meId !== null && ownerId === meId
}

/**
 * A short two-tone chime, synthesised rather than loaded.
 *
 * No audio file on purpose: this deployment vendors its fonts and makes zero
 * external requests, and an asset would also be one more thing to 404 in a
 * Tauri shell where the origin is `tauri://`. Web Audio needs no asset and no
 * network.
 *
 * Never throws. Browsers block audio until a user has interacted with the page,
 * and a rejected `resume()` must not take out the notification that came with
 * it.
 */
export function playChime(deps: NotifyDeps = {}): void {
  const Ctor = deps.AudioContextImpl ?? (typeof AudioContext !== 'undefined' ? AudioContext : undefined)
  if (!Ctor) return
  try {
    const ctx = new Ctor()
    const now = ctx.currentTime
    for (const [index, frequency] of [880, 1174.7].entries()) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = frequency
      // Shaped rather than square-edged: an abrupt start and stop clicks, and a
      // click is what people describe as "that horrible noise".
      const start = now + index * 0.09
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(0.08, start + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16)
      osc.connect(gain).connect(ctx.destination)
      osc.start(start)
      osc.stop(start + 0.18)
    }
    // Free the hardware once the sound has finished.
    window.setTimeout(() => void ctx.close().catch(() => {}), 600)
  } catch {
    // Audio is a courtesy. It never breaks the thing it accompanies.
  }
}

/**
 * Ask for desktop-notification permission.
 *
 * Called only when someone turns the toggle ON, never at page load: an
 * unprompted permission dialog is the single most disliked thing a web app
 * does, and Chrome now penalises origins that do it. Returns whether
 * notifications can actually be shown, so the caller can avoid saving a
 * preference the browser will silently ignore.
 */
export async function requestDesktopPermission(deps: NotifyDeps = {}): Promise<boolean> {
  const Impl = deps.NotificationImpl ?? (typeof Notification !== 'undefined' ? Notification : undefined)
  if (!Impl) return false
  if (Impl.permission === 'granted') return true
  if (Impl.permission === 'denied') return false
  try {
    return (await Impl.requestPermission()) === 'granted'
  } catch {
    return false
  }
}

/** Fire whichever notifications this person has enabled and is owed. */
export function notify(input: NotifyInput, deps: NotifyDeps = {}): void {
  if (!shouldNotify(input.meId, input.ownerId)) return

  if (input.sound) playChime(deps)

  if (!input.desktop) return
  const Impl = deps.NotificationImpl ?? (typeof Notification !== 'undefined' ? Notification : undefined)
  if (!Impl || Impl.permission !== 'granted') return

  // Suppressed while the window is focused — a desktop toast for the screen you
  // are already looking at is pure noise, and the rail has already updated.
  const hidden = deps.isHidden ?? (() => typeof document !== 'undefined' && document.hidden)
  if (!hidden()) return

  try {
    // Tagged by thread so a burst of messages replaces one toast rather than
    // stacking a wall of them.
    new Impl(input.title, { body: input.body, tag: input.title })
  } catch {
    // Same reasoning as the chime: a courtesy, never a failure path.
  }
}
